import { useCallback, useEffect, useRef, useState } from 'react';

const DEFAULT_STALE_MS = 60_000;

// Module-level cache: JSON.stringify(queryKey)+token → { data, ts, promise }
const queryCache = new Map();

function makeCacheKey(queryKey, token) {
  return JSON.stringify(queryKey) + '::' + (token || '');
}

function getToken() {
  return sessionStorage.getItem('mentorToken') || '';
}

export function invalidateQuery(queryKey) {
  const token = getToken();
  const key = makeCacheKey(queryKey, token);
  queryCache.delete(key);
}

export function invalidateQueriesMatching(prefix) {
  const token = getToken();
  const prefixStr = JSON.stringify(prefix).slice(0, -1); // match array prefix
  for (const key of queryCache.keys()) {
    if (key.startsWith(prefixStr) && key.includes('::' + token)) {
      queryCache.delete(key);
    }
  }
}

export function clearAllQueryCache() {
  queryCache.clear();
}

/** Seed cache after boot so useMentorQuery skips a duplicate cold fetch. */
export function seedMentorQuery(queryKey, data) {
  const token = getToken();
  const key = makeCacheKey(queryKey, token);
  queryCache.set(key, { data, ts: Date.now() });
}

export const APPROVALS_EVAL_QUERY_KEY = ['mentor', 'evaluations', 'approvals'];
export const YEAR2_APPROVALS_QUERY_KEY = ['mentor', 'year2', 'evaluations', 'approvals'];

/**
 * useMentorQuery(queryKey, fetcher, options)
 *
 * queryKey  — string[] e.g. ['mentor','cohorts']
 * fetcher   — () => Promise<data>
 * options:
 *   enabled      — boolean (default true); when false, no fetch is made
 *   staleTimeMs  — ms before cache entry is considered stale (default 60_000)
 *   caller       — string label for debug logging
 *
 * Returns { data, error, isLoading, isStale, refetch }
 */
export function useMentorQuery(queryKey, fetcher, options = {}) {
  const {
    enabled = true,
    staleTimeMs = DEFAULT_STALE_MS,
    caller = '',
  } = options;

  const token = getToken();
  const cacheKey = makeCacheKey(queryKey, token);

  const getInitialState = () => {
    const entry = queryCache.get(cacheKey);
    if (entry && entry.data !== undefined && !(entry.data instanceof Promise)) {
      const isStale = Date.now() - entry.ts > staleTimeMs;
      return { data: entry.data, error: null, isLoading: false, isStale };
    }
    return { data: undefined, error: null, isLoading: enabled, isStale: false };
  };

  const [state, setState] = useState(getInitialState);
  const mountedRef = useRef(true);
  const queryKeyStr = JSON.stringify(queryKey);
  const prevKeyRef = useRef(queryKeyStr);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  const execute = useCallback(async (force = false) => {
    if (!enabled) return;

    const currentToken = getToken();
    const key = makeCacheKey(queryKey, currentToken);
    const entry = queryCache.get(key);

    if (!force && entry) {
      if (entry.data instanceof Promise) {
        // in-flight: share the promise
        setState(prev => ({ ...prev, isLoading: true, error: null }));
        try {
          const data = await entry.data;
          if (mountedRef.current) {
            setState({ data, error: null, isLoading: false, isStale: false });
          }
        } catch (error) {
          if (mountedRef.current) {
            setState(prev => ({ ...prev, error, isLoading: false }));
          }
        }
        return;
      }
      const isStale = Date.now() - entry.ts > staleTimeMs;
      if (!isStale) {
        if (mountedRef.current) {
          setState({ data: entry.data, error: null, isLoading: false, isStale: false });
        }
        return;
      }
    }

    setState(prev => ({ ...prev, isLoading: true, error: null }));

    const promise = fetcher();
    queryCache.set(key, { data: promise, ts: Date.now() });

    if (caller) {
      // eslint-disable-next-line no-console
      console.debug(`[mentorQuery] fetch ${caller}`, queryKey);
    }

    try {
      const data = await promise;
      queryCache.set(key, { data, ts: Date.now() });
      if (mountedRef.current) {
        setState({ data, error: null, isLoading: false, isStale: false });
      }
    } catch (error) {
      queryCache.delete(key);
      if (mountedRef.current) {
        setState(prev => ({ ...prev, error, isLoading: false }));
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryKeyStr, enabled, staleTimeMs, caller]);

  useEffect(() => {
    const keyChanged = prevKeyRef.current !== queryKeyStr;
    prevKeyRef.current = queryKeyStr;

    if (!enabled) {
      setState(prev => ({ ...prev, isLoading: false }));
      return;
    }

    const entry = queryCache.get(cacheKey);
    if (!keyChanged && entry && !(entry.data instanceof Promise)) {
      const isStale = Date.now() - entry.ts > staleTimeMs;
      if (!isStale) {
        setState({ data: entry.data, error: null, isLoading: false, isStale: false });
        return;
      }
    }

    execute();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryKeyStr, enabled]);

  const refetch = useCallback(() => execute(true), [execute]);

  return { data: state.data, error: state.error, isLoading: state.isLoading, isStale: state.isStale, refetch };
}
